import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Mic, PhoneOff, Sparkles } from "lucide-react";
import api from "./api";
import { Btn } from "./ui";

const SILENCE_MS = 1800;
const RMS_THRESHOLD = 12;

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result.split(",")[1]);
    r.readAsDataURL(blob);
  });
}

export default function VoiceMode({ caseId, advisorName, open, onClose, onUpdate, onRunAnalysis }) {
  const [phase, setPhase] = useState("starting"); // starting | listening | thinking | speaking | ready | error
  const [lastUser, setLastUser] = useState("");
  const [lastReply, setLastReply] = useState("");
  const activeRef = useRef(false);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const phaseRef = useRef("starting");
  phaseRef.current = phase;

  const cleanup = () => {
    activeRef.current = false;
    try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch {}
    recRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { audioRef.current?.pause(); } catch {}
    audioRef.current = null;
    try { ctxRef.current?.close(); } catch {}
    ctxRef.current = null;
  };

  useEffect(() => {
    if (!open) return;
    activeRef.current = true;
    setPhase("starting");
    setLastUser("");
    setLastReply("");
    startListening();
    return cleanup;
  }, [open]);

  const startListening = async () => {
    if (!activeRef.current) return;
    try {
      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      const stream = streamRef.current;

      if (!ctxRef.current) {
        ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const src = ctxRef.current.createMediaStreamSource(stream);
        const analyser = ctxRef.current.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        ctxRef.current.analyser = analyser;
      }
      await ctxRef.current.resume();

      const chunks = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        if (!activeRef.current || chunks.length === 0) return;
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 2000) {
          startListening();
          return;
        }
        await processTurn(blob);
      };
      recRef.current = rec;
      rec.start(200);
      setPhase("listening");

      const analyser = ctxRef.current.analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      let speechStarted = false;
      let silenceFor = 0;
      let elapsed = 0;
      const poll = setInterval(() => {
        if (!activeRef.current || recRef.current !== rec) {
          clearInterval(poll);
          return;
        }
        analyser.getByteTimeDomainData(data);
        let max = 0;
        for (let i = 0; i < data.length; i++) max = Math.max(max, Math.abs(data[i] - 128));
        elapsed += 100;
        if (max > RMS_THRESHOLD) {
          speechStarted = true;
          silenceFor = 0;
        } else if (speechStarted) {
          silenceFor += 100;
        }
        const longEnough = elapsed > 30000; // safety cap per turn
        if ((speechStarted && silenceFor >= SILENCE_MS && elapsed > 800) || longEnough) {
          clearInterval(poll);
          try { rec.stop(); } catch {}
        }
      }, 100);
    } catch {
      setPhase("error");
      toast.error("Microphone unavailable — check browser permissions.");
    }
  };

  const processTurn = async (blob) => {
    if (!activeRef.current) return;
    setPhase("thinking");
    try {
      const b64 = await blobToBase64(blob);
      const t = await api.post("/audio/transcribe", { audio_base64: b64, mime: blob.type });
      const text = (t.data.text || "").trim();
      if (!text) {
        startListening();
        return;
      }
      setLastUser(text);
      const r = await api.post(`/cases/${caseId}/interview`, { user_message: text, input_type: "VOICE" });
      onUpdate?.();
      const reply = (r.data.assistant_message || "").trim();
      if (r.data.ready_for_analysis) {
        if (reply) setLastReply(reply);
        setPhase("ready");
        return;
      }
      if (!reply) {
        startListening();
        return;
      }
      setLastReply(reply);
      await speak(reply);
      startListening();
    } catch (err) {
      if (!activeRef.current) return;
      toast.error(err.response?.data?.detail || "Voice turn failed — trying to recover.");
      startListening();
    }
  };

  const speak = async (text) => {
    if (!activeRef.current) return;
    setPhase("speaking");
    try {
      const r = await api.post("/audio/speak", { text: text.slice(0, 1500) });
      const audio = new Audio(`data:${r.data.mime};base64,${r.data.audio_base64}`);
      audioRef.current = audio;
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
    } catch {
      // speech output is best-effort; keep the loop alive
    }
  };

  const close = () => {
    cleanup();
    onClose();
  };

  const labels = {
    starting: "Taking a seat…",
    listening: "Listening — speak freely",
    thinking: "The advisor is thinking…",
    speaking: `${advisorName || "Advisor"} is speaking…`,
    ready: "The brief is ready",
    error: "Microphone unavailable",
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="voice-mode-overlay"
          className="fixed inset-0 z-[100] bg-[#141312] flex flex-col"
        >
          <div className="flex items-center justify-between px-6 md:px-12 py-6">
            <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]">
              Voice Mode · {advisorName || "Advisor"}
            </p>
            <button data-testid="voice-close-btn" onClick={close} className="text-stone-400 hover:text-white transition-colors duration-300">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center max-w-2xl mx-auto w-full">
            <motion.div
              animate={phase === "listening" ? { scale: [1, 1.08, 1] } : {}}
              transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              className={`w-28 h-28 rounded-full flex items-center justify-center mb-10 border transition-colors duration-500 ${
                phase === "listening"
                  ? "bg-[#B5A58A]/15 border-[#B5A58A]"
                  : phase === "speaking" || phase === "thinking"
                  ? "bg-[#B5A58A]/10 border-[#B5A58A]/40"
                  : "bg-white/[0.04] border-white/10"
              }`}
            >
              {phase === "thinking" ? (
                <span className="w-8 h-8 rounded-full border-2 border-[#B5A58A]/30 border-t-[#B5A58A] animate-spin" />
              ) : (
                <Mic className={`w-10 h-10 ${phase === "listening" ? "text-[#B5A58A]" : "text-stone-500"}`} strokeWidth={1.25} />
              )}
            </motion.div>

            <p data-testid="voice-status" className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-stone-400 mb-8">
              {labels[phase]}
            </p>

            {lastUser && (
              <div className="mb-4 w-full">
                <p className="font-mono-label text-[8px] tracking-[0.25em] uppercase text-stone-500 mb-2">You said</p>
                <p className="text-sm leading-relaxed text-stone-400">{lastUser}</p>
              </div>
            )}
            {lastReply && (
              <div className="w-full">
                <p className="font-mono-label text-[8px] tracking-[0.25em] uppercase text-stone-500 mb-2">{advisorName || "Advisor"}</p>
                <p className="font-serif-display text-2xl leading-snug text-white">{lastReply}</p>
              </div>
            )}

            {phase === "ready" && (
              <div className="mt-10">
                <Btn testid="voice-run-analysis-btn" onClick={() => { close(); onRunAnalysis?.(); }}>
                  <Sparkles className="w-3.5 h-3.5 inline mr-2" strokeWidth={1.5} /> Run the analysis
                </Btn>
              </div>
            )}
          </div>

          <div className="px-6 md:px-12 py-8 flex justify-center">
            <button
              data-testid="voice-end-btn"
              onClick={close}
              className="flex items-center gap-3 border border-white/15 rounded-full px-7 py-3.5 font-mono-label text-[10px] tracking-[0.25em] uppercase text-stone-300 hover:border-[#B37D3B] hover:text-[#D09B5F] transition-colors duration-300"
            >
              <PhoneOff className="w-4 h-4" strokeWidth={1.5} /> Leave voice mode
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
